import { useState } from 'react';
import type { TaskStatus, TaskSummary } from '@todo/shared';
import { ApiError } from '../api/client';
import { taskApi } from '../api/task.api';

/**
 * The four mutations every task list page repeats, with the busy-row and
 * error plumbing done once. `mutate` is the page's own "this task changed"
 * handler - pages differ only in what they do with the fresh row (replace it,
 * drop it from a filtered list, regroup...), so that part stays theirs.
 */
export function useTaskActions(
  mutate: (task: TaskSummary) => void,
  onError: (message: string) => void,
) {
  const [busyId, setBusyId] = useState<string | null>(null);

  async function run(id: string, fallback: string, fn: () => Promise<TaskSummary>) {
    setBusyId(id);
    try {
      mutate(await fn());
      return true;
    } catch (err) {
      onError(err instanceof ApiError ? err.message : fallback);
      return false;
    } finally {
      setBusyId(null);
    }
  }

  return {
    busyId,
    toggleImportant: (task: TaskSummary) =>
      run(task.id, 'Could not update Important', () => taskApi.setImportant(task.id, !task.isImportant)),
    setStatus: (task: TaskSummary, next: TaskStatus) =>
      run(task.id, 'Could not update the status', () => taskApi.setStatus(task.id, next)),
    /** Circle check: DONE <-> reopen. */
    toggleDone: (task: TaskSummary) =>
      run(task.id, 'Could not update the status', () =>
        taskApi.setStatus(task.id, task.status === 'DONE' ? 'PENDING' : 'DONE')),
  };
}
